/**
 * Fase 1 — Gjenbrukbar wizard-overlay for form-fyllings-flyter (prøvesett + kursinstans).
 *
 * Forskjellig fra src/src/components/help/Wizard.tsx som er for FØRSTEGANGS-hjelpevisninger
 * (dismissable, lagrer preference). Denne er for OPPRETTELSES-flyter (ingen preference-lagring,
 * stegvalidering per felt, flere final-handlinger som "Lagre og start neste veiviser").
 *
 * Design:
 *   - Portal-rendret (overlay over hele siden)
 *   - max-w-5xl på desktop, full-screen under 768px
 *   - Horisontal stegindikator med klikkbare steg (hvis besøkt tidligere)
 *   - Forrige/Neste knapper i footer
 *   - På siste steg: `finalActions` (f.eks. "Lagre utkast", "Lagre og publiser", "Lagre + start kurs-veiviser")
 *   - Esc lukker (med bekreftelse hvis hasChanges)
 *   - ARIA: role=dialog, aria-modal, aria-labelledby
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, X, Loader2 } from 'lucide-react';
import { useConfirm } from '../ui';

export interface WizardStepDef {
  key: string;
  label: string;
  description?: string;
  content: ReactNode;
  /** true = kan gå videre, false = blokker neste-knapp stille, string = blokker + vis feilmelding */
  canAdvance?: boolean | string;
}

export interface WizardFinalAction {
  key: string;
  label: string;
  variant?: 'primary' | 'secondary' | 'success' | 'ghost';
  icon?: ReactNode;
  onClick: () => Promise<void> | void;
  loading?: boolean;
  disabled?: boolean;
  disabledReason?: string;
}

export interface WizardModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  steps: WizardStepDef[];
  currentStep: number;
  onStepChange: (step: number) => void;
  finalActions: WizardFinalAction[];
  /** Varsler ved lukk hvis bruker har gjort endringer. Default false. */
  hasChanges?: boolean;
  /** Egendefinert tekst i "forlat uten å lagre"-bekreftelse */
  unsavedChangesWarning?: string;
}

const variantClasses: Record<NonNullable<WizardFinalAction['variant']>, string> = {
  primary: 'bg-kf-blue-600 hover:bg-kf-blue-700 text-white',
  secondary: 'bg-white border border-kf-gray-300 text-kf-gray-700 hover:bg-kf-gray-50',
  success: 'bg-emerald-600 hover:bg-emerald-700 text-white',
  ghost: 'text-kf-gray-700 hover:text-kf-gray-900 hover:bg-kf-gray-50',
};

export default function WizardModal({
  open,
  onClose,
  title,
  subtitle,
  icon,
  steps,
  currentStep,
  onStepChange,
  finalActions,
  hasChanges = false,
  unsavedChangesWarning = 'Du har endringer som ikke er lagret. Vil du virkelig lukke veiviseren?',
}: WizardModalProps) {
  const [visitedSteps, setVisitedSteps] = useState<Set<number>>(new Set([0]));
  const [showAdvanceError, setShowAdvanceError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const { confirm } = useConfirm();

  // Reset visited når veiviser åpnes
  useEffect(() => {
    if (open) setVisitedSteps(new Set([currentStep]));
  }, [open]);

  // Merk hvert besøkte steg + scroll body til topp ved steg-bytte
  // (uten dette starter neste steg midt i innholdet hvis forrige steg var langt)
  useEffect(() => {
    setVisitedSteps((prev) => {
      const next = new Set(prev);
      next.add(currentStep);
      return next;
    });
    setShowAdvanceError(null);
    if (bodyRef.current) {
      bodyRef.current.scrollTop = 0;
    }
  }, [currentStep]);

  // Esc-handler
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, hasChanges]);

  // Lås body-scroll mens veiviseren er åpen — ellers scroller siden bak når
  // musehjul brukes utenfor eller når scroll når bunnen av veiviser-innholdet.
  useEffect(() => {
    if (!open) return;
    const originalOverflow = document.body.style.overflow;
    const originalPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = originalOverflow;
      document.body.style.paddingRight = originalPaddingRight;
    };
  }, [open]);

  if (!open || steps.length === 0) return null;

  const step = steps[currentStep];
  const isFirst = currentStep === 0;
  const isLast = currentStep === steps.length - 1;
  const canGoNext = step.canAdvance !== false && typeof step.canAdvance !== 'string';

  const handleClose = async () => {
    if (hasChanges) {
      const ok = await confirm({
        title: 'Lukke veiviseren?',
        message: unsavedChangesWarning,
        variant: 'warning',
        confirmLabel: 'Lukk uten å lagre',
        cancelLabel: 'Fortsett å redigere',
      });
      if (!ok) return;
    }
    onClose();
  };

  const handleNext = () => {
    if (step.canAdvance === false) {
      return;
    }
    if (typeof step.canAdvance === 'string') {
      setShowAdvanceError(step.canAdvance);
      return;
    }
    setShowAdvanceError(null);
    if (currentStep < steps.length - 1) {
      onStepChange(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setShowAdvanceError(null);
      onStepChange(currentStep - 1);
    }
  };

  const handleStepClick = (idx: number) => {
    // Kan navigere til ferdigbesøkte steg; nye steg krever Neste-knapp
    if (visitedSteps.has(idx)) {
      setShowAdvanceError(null);
      onStepChange(idx);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[8500] flex items-stretch sm:items-center justify-center sm:p-4 bg-kf-gray-900/60 backdrop-blur-[2px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="bg-white sm:rounded-xl shadow-2xl w-full sm:max-w-5xl flex flex-col h-full sm:h-auto sm:max-h-[92vh]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wizard-modal-title"
      >
        {/* Header */}
        <header className="px-4 sm:px-6 py-4 border-b border-kf-gray-200 bg-gradient-to-r from-kf-blue-50 to-kf-orange-50/50 sm:rounded-t-xl flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-kf-blue-700 font-semibold mb-1">
                {icon}
                <span>Veiviser · Steg {currentStep + 1} av {steps.length}</span>
              </div>
              <h2 id="wizard-modal-title" className="text-lg sm:text-xl font-semibold text-kf-gray-900 leading-tight">
                {title}
              </h2>
              {subtitle && <p className="text-sm text-kf-gray-600 mt-1">{subtitle}</p>}
            </div>
            <button
              onClick={handleClose}
              className="shrink-0 p-1.5 rounded hover:bg-white/80 text-kf-gray-600 transition-colors"
              aria-label="Lukk veiviser"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Stegindikator — numererte kuler på desktop, fremdriftsbar på mobil */}
          <div className="mt-4">
            <div className="hidden sm:flex items-center gap-1">
              {steps.map((s, i) => {
                const isCurrent = i === currentStep;
                const isVisited = visitedSteps.has(i);
                const isComplete = i < currentStep;
                return (
                  <div key={s.key} className="flex items-center flex-1 last:flex-initial">
                    <button
                      type="button"
                      onClick={() => handleStepClick(i)}
                      disabled={!isVisited}
                      className={`flex items-center gap-2 min-w-0 ${isVisited ? 'cursor-pointer' : 'cursor-not-allowed'}`}
                      aria-current={isCurrent ? 'step' : undefined}
                    >
                      <span
                        className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors ${
                          isCurrent
                            ? 'bg-kf-orange-500 text-white ring-2 ring-kf-orange-200'
                            : isComplete
                            ? 'bg-kf-blue-600 text-white'
                            : isVisited
                            ? 'bg-kf-blue-100 text-kf-blue-700'
                            : 'bg-kf-gray-100 text-kf-gray-400'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span
                        className={`text-xs font-medium truncate hidden md:inline ${
                          isCurrent
                            ? 'text-kf-gray-900'
                            : isVisited
                            ? 'text-kf-gray-700'
                            : 'text-kf-gray-400'
                        }`}
                      >
                        {s.label}
                      </span>
                    </button>
                    {i < steps.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-2 ${
                          i < currentStep ? 'bg-kf-blue-600' : 'bg-kf-gray-200'
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            {/* Mobil-progressbar */}
            <div className="sm:hidden flex gap-1" role="progressbar" aria-valuenow={currentStep + 1} aria-valuemax={steps.length}>
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded transition-colors ${
                    i < currentStep ? 'bg-kf-blue-600' : i === currentStep ? 'bg-kf-orange-500' : 'bg-kf-gray-200'
                  }`}
                />
              ))}
            </div>
          </div>
        </header>

        {/* Body */}
        <div
          ref={bodyRef}
          className="flex-1 overflow-y-auto px-4 sm:px-6 py-5"
          style={{ overscrollBehavior: 'contain' }}
        >
          <div className="mb-4">
            <h3 className="text-lg sm:text-xl font-semibold text-kf-gray-900">{step.label}</h3>
            {step.description && (
              <p className="text-sm text-kf-gray-600 mt-1">{step.description}</p>
            )}
          </div>

          {showAdvanceError && (
            <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900">
              <strong className="font-semibold">For å gå videre:</strong> {showAdvanceError}
            </div>
          )}

          <div>{step.content}</div>
        </div>

        {/* Footer */}
        <footer className="px-4 sm:px-6 py-3 sm:py-4 border-t border-kf-gray-200 bg-kf-gray-50 sm:rounded-b-xl flex-shrink-0 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3">
          <div className="flex items-center justify-between sm:justify-start gap-2">
            <button
              type="button"
              onClick={handlePrev}
              disabled={isFirst}
              className="px-3 py-1.5 text-sm bg-white border border-kf-gray-300 rounded flex items-center gap-1 disabled:opacity-40 hover:bg-kf-gray-50 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Forrige
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="px-3 py-1.5 text-sm text-kf-gray-700 hover:text-kf-gray-900 hover:bg-white rounded transition-colors sm:ml-2"
            >
              Avbryt
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap sm:justify-end">
            {!isLast && (
              <button
                type="button"
                onClick={handleNext}
                disabled={!canGoNext && step.canAdvance === false}
                className="px-4 py-1.5 text-sm bg-kf-blue-600 hover:bg-kf-blue-700 text-white rounded flex items-center gap-1 disabled:opacity-50 transition-colors"
              >
                Neste
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
            {isLast && finalActions.map((action) => {
              const variant = action.variant || 'primary';
              return (
                <button
                  key={action.key}
                  type="button"
                  onClick={() => action.onClick()}
                  disabled={action.disabled || action.loading}
                  title={action.disabled ? action.disabledReason : undefined}
                  className={`px-4 py-1.5 text-sm rounded flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variantClasses[variant]}`}
                >
                  {action.loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : action.icon ? (
                    action.icon
                  ) : null}
                  {action.label}
                </button>
              );
            })}
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
}
